<template>
  <aside
    id="sidebar"
    v-if="isAuthenticated"
    :class="{ expand: isExpanded, opening: isOpening }"
    role="navigation"
    aria-label="Боковое меню"
  >
    <div class="brand-row">
      <div class="brand-title" v-if="isExpanded && !isOpening" aria-label="STREAMMEDIA">
        <span class="brand-word brand-stream">STREAM</span><span class="brand-word brand-media">MEDIA</span>
      </div>

      <button
        class="toggle-btn"
        type="button"
        @click="toggleSidebar"
        :aria-expanded="isExpanded.toString()"
        aria-controls="sidebar"
        aria-label="Переключить меню"
      >
        <img
          :src="isExpanded ? require('@/assets/icons/toggle-box-left.png') : require('@/assets/icons/toggle-box-right.png')"
          width="22"
          height="22"
          alt="toggle"
        />
      </button>
    </div>

    <ul class="sidebar-nav">
      <li class="sidebar-item" v-if="isAdmin">
        <a href="/admin" class="sidebar-link" :class="{ active: isActive('/admin') }" title="Администрирование">
          <i class="lni lni-shield"></i>
          <span>Администрирование</span>
        </a>
      </li>
      <li class="sidebar-item">
        <a href="/dashboard" class="sidebar-link" :class="{ active: isActive('/dashboard') }" title="Дашборд">
          <i class="lni lni-stats-up"></i>
          <span>Дашборд</span>
        </a>
      </li>

      <li class="sidebar-item" v-if="false">
        <a href="/admin" class="sidebar-link" :class="{ active: isActive('/admin') }" title="Администрирование">
          <i class="lni lni-shield"></i>
          <span>Администрирование</span>
        </a>
      </li>

      <li class="sidebar-item">
        <a href="/profile" class="sidebar-link" :class="{ active: isActive('/profile') || isActive('/lk') }" title="Профиль">
          <i class="lni lni-user"></i>
          <span>Профиль</span>
        </a>
      </li>

      <li class="sidebar-item">
        <a href="/streams" class="sidebar-link" :class="{ active: isActive('/streams') }" title="Стримы">
          <i class="lni lni-chevron-right-circle"></i>
          <span>Стримы</span>
        </a>
      </li>

      <li class="sidebar-item">
        <a href="/cmaker/runs" class="sidebar-link" :class="{ active: isActive('/cmaker/runs') }" title="Расписание">
          <i class="lni lni-pulse"></i>
          <span>Расписание</span>
        </a>
      </li>

      <li class="sidebar-item">
        <a href="/faq" class="sidebar-link" :class="{ active: isActive('/faq') }" title="Справочник">
          <i class="lni lni-information"></i>
          <span>Справочник</span>
        </a>
      </li>

      <li class="sidebar-item">
        <a href="/tools" class="sidebar-link" :class="{ active: isActive('/tools') || isActive('/settings') }" title="Инструменты">
          <i class="lni lni-cog"></i>
          <span>Инструменты</span>
        </a>
      </li>
    </ul>

    <div class="sidebar-footer">
      <button class="logout-icon-btn" @click="logout" title="Выйти" aria-label="Выйти">
        <i class="lni lni-exit"></i>
      </button>

      <div v-if="currentUser?.username" class="login-inline" :title="currentUser.username">
        {{ currentUser.username }}
      </div>
    </div>
  </aside>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { clearAuthTokens } from '@/utils/authToken'

export default {
  name: 'SideBar',
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const currentUser = computed(() => store.getters.currentUser)
    const isAdmin = computed(() => store.getters.isAdmin)

    const STORAGE_KEY = 'sidebar:expanded'

    const isAuthenticated = computed(() => store.getters.isAuthenticated)
    const isExpanded = ref(true)
    const isOpening = ref(false)
    let openTimer = null

    function setAppVh() {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--app-vh', `${vh}px`)
    }

    onMounted(() => {
      setAppVh()
      window.addEventListener('resize', setAppVh)

      try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (raw !== null) isExpanded.value = raw === 'true'
      } catch {
        // no-op
      }
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', setAppVh)
    })

    watch(isExpanded, val => {
      try {
        localStorage.setItem(STORAGE_KEY, String(val))
      } catch {
        // no-op
      }
      if (val) {
        isOpening.value = true
        clearTimeout(openTimer)
        openTimer = setTimeout(() => {
          isOpening.value = false
        }, 150)
      } else {
        clearTimeout(openTimer)
        isOpening.value = false
      }
    })

    onBeforeUnmount(() => {
      clearTimeout(openTimer)
    })

    const toggleSidebar = () => {
      isExpanded.value = !isExpanded.value
    }

    const isActive = base => {
      const p = route.path || ''
      return p === base || p.startsWith(base + '/')
    }

    const logout = async () => {
      try {
        // Credentialed logout is the SOLE remaining exception across
        // the codebase — needed so the browser applies astra's
        // `Set-Cookie: jwt=; Max-Age=0` cleanup of any legacy cookie.
        // See ADR-15.
        await axios.post('/api/account/logout', {}, { withCredentials: true })
      } catch {
        // best-effort; continue cleanup even on network failure
      }
      clearAuthTokens()
      store.commit('clearUser')
      router.push('/login')
    }

    return { isAuthenticated, isExpanded, toggleSidebar, isActive, logout, currentUser, isOpening, isAdmin }
  },
}
</script>

<style>
:root {
  --app-vh: 1vh;
}
</style>

<style scoped>
a {
  text-decoration: none;
}

li {
  list-style: none;
}

#sidebar {
  --sb-bg-1: #0c1327;
  --sb-bg-2: #141e38;
  --sb-border: rgba(168, 192, 255, 0.18);
  --sb-accent: #8ab4ff;
  --sb-accent-2: #5a88ff;
  --sb-text: #e9efff;
  --sb-muted: #aebde5;
  --sb-hover: rgba(138, 180, 255, 0.13);
  --sb-active: rgba(138, 180, 255, 0.2);
  --sb-danger: #ff8b8b;
  width: 70px;
  min-width: 70px;
  height: calc(var(--app-vh, 1vh) * 100);
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  color: var(--sb-text);
  border-right: 1px solid var(--sb-border);
  background: linear-gradient(180deg, var(--sb-bg-2), var(--sb-bg-1) 42%, #0a1022 100%);
  box-shadow: 8px 0 28px rgba(8, 15, 35, 0.35);
  transition: width 0.24s ease-in-out, min-width 0.24s ease-in-out;
}

#sidebar.expand {
  width: 226px;
  min-width: 226px;
}

.brand-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 8px 11px;
  border-bottom: 1px solid rgba(168, 192, 255, 0.16);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.035), transparent);
}

.brand-title {
  display: inline-flex;
  align-items: center;
  gap: 0.14ch;
  margin-left: 0.45rem;
  user-select: none;
  letter-spacing: 0.04em;
  font-size: 17px;
  line-height: 17px;
  height: 17px;
}

.brand-word.brand-stream {
  font-weight: 800;
  color: var(--sb-text);
}

.brand-word.brand-media {
  font-weight: 700;
  letter-spacing: 0.1em;
  margin-left: 0.15ch;
  background: linear-gradient(90deg, #9fc0ff, var(--sb-accent-2));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.toggle-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: 1px solid rgba(168, 192, 255, 0.2);
  background: rgba(255, 255, 255, 0.04);
  transition: background-color 0.16s ease, transform 0.12s ease, box-shadow 0.16s ease;
}

.toggle-btn img {
  width: 20px;
  height: 20px;
  display: block;
}

.toggle-btn:hover {
  background: var(--sb-hover);
}

.toggle-btn:active {
  transform: scale(0.98);
}

.toggle-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(138, 180, 255, 0.22);
}

.sidebar-nav {
  flex: 1 1 auto;
  padding: 0.42rem 0.5rem 0.8rem;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(169, 192, 244, 0.45) transparent;
}

.sidebar-nav::-webkit-scrollbar {
  width: 8px;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: rgba(169, 192, 244, 0.45);
  border-radius: 8px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-item + .sidebar-item {
  margin-top: 2px;
}

a.sidebar-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.62rem;
  margin: 0;
  padding: 0.5rem 0.56rem;
  border-radius: 11px;
  color: var(--sb-text);
  font-size: 0.91rem;
  white-space: nowrap;
  transition: background-color 0.16s ease, color 0.16s ease, transform 0.12s ease;
}

a.sidebar-link:hover {
  background: var(--sb-hover);
  color: #ffffff;
}

.sidebar-link i {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.07);
  color: var(--sb-muted);
  font-size: 1.02rem;
  transition: background-color 0.16s ease, color 0.16s ease;
}

a.sidebar-link span {
  position: relative;
  top: 1px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

#sidebar:not(.expand) a.sidebar-link span {
  display: none;
}

a.sidebar-link.active {
  color: #ffffff;
  background: linear-gradient(90deg, var(--sb-active), rgba(138, 180, 255, 0.08));
}

a.sidebar-link.active i {
  color: #ffffff;
  background: linear-gradient(180deg, rgba(138, 180, 255, 0.36), rgba(138, 180, 255, 0.2));
  box-shadow: inset 0 0 0 1px rgba(173, 199, 255, 0.34);
}

a.sidebar-link.active::before {
  content: '';
  position: absolute;
  left: -3px;
  top: 6px;
  bottom: 6px;
  width: 3px;
  border-radius: 2px;
  background: linear-gradient(180deg, #9ac0ff, #5a88ff);
}

.sidebar-footer {
  min-height: 54px;
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 1px solid rgba(168, 192, 255, 0.16);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent);
}

.logout-icon-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  border: 1px solid rgba(255, 139, 139, 0.24);
  background: rgba(255, 139, 139, 0.08);
  color: var(--sb-danger);
  cursor: pointer;
  transition: background-color 0.16s ease, color 0.16s ease, transform 0.12s ease;
}

.logout-icon-btn:hover {
  background: rgba(255, 139, 139, 0.16);
  color: #ffd0d0;
}

.logout-icon-btn:active {
  transform: scale(0.98);
}

.logout-icon-btn i {
  font-size: 1rem;
  line-height: 1;
}

.login-inline {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 2px;
  color: var(--sb-muted);
  font-size: 0.82rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

#sidebar:not(.expand) .sidebar-footer {
  justify-content: center;
}

#sidebar:not(.expand) .login-inline {
  display: none;
}

#sidebar:not(.expand) .brand-row {
  justify-content: center;
}

#sidebar:not(.expand) .toggle-btn {
  position: relative;
  left: 1px;
}

#sidebar.expand.opening .brand-row {
  justify-content: flex-start;
}

#sidebar:not(.expand) .brand-row {
  justify-content: center;
}

#sidebar:not(.expand) a.sidebar-link {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
</style>
